import { gaVisitorId } from "@/components/util";

/**
 * Syncs the Google Analytics visitor ID to the backend.
 *
 * This function is called on important pages to keep the visitor ID current.
 * It implements:
 * - localStorage-based rate limiting (1 hour minimum between syncs)
 * - Random delay to prevent thundering herd from multiple tabs
 * - Fire-and-forget pattern (no error handling needed)
 */
export async function syncVisitorId(): Promise<void> {
  // Only run in browser environment
  if (typeof window === "undefined") return;

  // Get visitor ID from GA cookie
  const vid = gaVisitorId();
  if (!vid) return; // No GA cookie, nothing to sync

  // Check localStorage for last sync time
  const lastSyncKey = "vid_sync_time";
  const lastSync = localStorage.getItem(lastSyncKey);
  const now = Date.now();
  const ONE_HOUR = 3600000; // milliseconds

  // Skip if synced within last hour
  if (lastSync && now - parseInt(lastSync) < ONE_HOUR) {
    return;
  }

  // Random delay 1-5 seconds to prevent multiple tabs from syncing simultaneously
  const randomDelay = 1000 + Math.random() * 4000;
  await new Promise((resolve) => setTimeout(resolve, randomDelay));

  // Re-check localStorage (another tab might have synced while we were waiting)
  const recheckedSync = localStorage.getItem(lastSyncKey);
  if (recheckedSync && now - parseInt(recheckedSync) < ONE_HOUR) {
    return;
  }

  // Send sync request (fire-and-forget)
  fetch("/web_api/accounts/sync_visitor_id", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ vid }),
  })
    .then((response) => {
      // Update localStorage after request completes (even if rate limited)
      // This prevents unnecessary retries when we hit the server rate limit
      if (response.ok || response.status === 429) {
        localStorage.setItem(lastSyncKey, now.toString());
      }
    })
    .catch(() => {
      // Silently fail - we'll retry on next page load
    });
}
